import React from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTheme } from '../../context/ThemeContext';

const TimeSeriesChart = ({ data, options = {}, fieldConfig = {} }) => {
  const { isDark } = useTheme();
  const [hiddenSeries, setHiddenSeries] = React.useState(new Set());
  
  if (!data || data.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        color: isDark ? '#8b949e' : '#8c8c8c',
        fontSize: 13
      }}>
        No data
      </div>
    );
  }

  // Get all series keys (exclude 'time' and 'timestamp')
  const seriesKeys = Object.keys(data[0]).filter(key => key !== 'time' && key !== 'timestamp');

  // Grafana-like color palette
  const colors = isDark 
    ? ['#73BF69', '#F2CC0C', '#3274D9', '#E0B400', '#F2495C', '#5794F2', '#B877D9', '#705DA0', '#447EBC', '#C15C17']
    : ['#7EB26D', '#EAB839', '#6ED0E0', '#EF843C', '#E24D42', '#1F78C1', '#BA43A9', '#705DA0', '#508642', '#CCA300'];

  // Determine chart style from options
  const drawStyle = options.drawStyle || 'line';
  const fillOpacity = options.fillOpacity ?? (isDark ? 0.15 : 0.25);
  const lineWidth = options.lineWidth || 1.5;
  const showLegend = options.legend?.show ?? true;
  const showPoints = options.showPoints ?? false;

  const ChartComponent = drawStyle === 'line' ? LineChart : AreaChart;

const handleLegendClick = (e) => {
  const clickedKey = e.dataKey;
  
  // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
  const isMultiSelect = e.ctrlKey || e.metaKey;
  
  if (isMultiSelect) {
    // Multi-select: toggle individual line
    setHiddenSeries(prev => {
      const newHidden = new Set(prev);
      if (newHidden.has(clickedKey)) {
        newHidden.delete(clickedKey);
      } else {
        newHidden.add(clickedKey);
      }
      return newHidden;
    });
  } else {
    // Single click: show only this one (or show all if already selected)
    if (hiddenSeries.size === seriesKeys.length - 1 && !hiddenSeries.has(clickedKey)) {
      setHiddenSeries(new Set()); // Show all
    } else {
      const newHidden = new Set(seriesKeys.filter(key => key !== clickedKey));
      setHiddenSeries(newHidden); // Show only clicked
    }
  }
};

  // Custom tooltip styling
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    
    return (
      <div style={{
        background: isDark ? 'rgba(22, 27, 34, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        border: isDark ? '1px solid #30363d' : '1px solid #d9d9d9',
        borderRadius: 4,
        padding: '8px 12px',
        fontSize: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }}>
        <div style={{ 
          marginBottom: 6, 
          fontWeight: 500,
          color: isDark ? '#e6edf3' : '#262626'
        }}>
          {label}
        </div>
        {payload.map((entry, index) => (
          <div key={index} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 8,
            marginTop: 4,
            color: isDark ? '#adbac7' : '#595959'
          }}>
            <div style={{
              width: 12,
              height: 2,
              background: entry.color,
              borderRadius: 1
            }} />
            <span>{entry.name}:</span>
            <span style={{ fontWeight: 500, color: isDark ? '#e6edf3' : '#262626' }}>
              {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ChartComponent 
        data={data}
        margin={{ top: showLegend ? 5 : 10, right: 10, left: 0, bottom: 5 }}
      >
        <CartesianGrid 
          strokeDasharray="3 3" 
          stroke={isDark ? '#30363d' : '#e5e5e5'}
          strokeOpacity={0.5}
        />
        <XAxis 
          dataKey="time" 
          style={{ 
            fontSize: '11px',
            fill: isDark ? '#8b949e' : '#8c8c8c'
          }}
          stroke={isDark ? '#30363d' : '#d9d9d9'}
          tick={{ fill: isDark ? '#8b949e' : '#8c8c8c' }}
        />
        <YAxis 
          style={{ 
            fontSize: '11px',
            fill: isDark ? '#8b949e' : '#8c8c8c'
          }}
          stroke={isDark ? '#30363d' : '#d9d9d9'}
          tick={{ fill: isDark ? '#8b949e' : '#8c8c8c' }}
          width={45}
        />
        <Tooltip content={<CustomTooltip />} />
        {showLegend && (
          <Legend 
            wrapperStyle={{ 
              fontSize: '11px', 
              cursor: 'pointer',
              paddingTop: 8,
              color: isDark ? '#adbac7' : '#595959'
            }}
            verticalAlign="top"
            height={32}
            onClick={handleLegendClick}
            iconType="line"
          />
        )}
        {seriesKeys.map((key, index) => {
          if (hiddenSeries.has(key)) return null;
          const color = colors[index % colors.length];
          
          if (drawStyle === 'line') {
            return (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={color}
                strokeWidth={lineWidth}
                name={key}
                connectNulls
                dot={showPoints}
                activeDot={{ r: 4, strokeWidth: 0 }}
                animationDuration={300}
              />
            );
          } else {
            return (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={color}
                fill={color}
                fillOpacity={fillOpacity}
                strokeWidth={lineWidth}
                name={key}
                connectNulls
                animationDuration={300}
              />
            );
          }
        })}
      </ChartComponent>
    </ResponsiveContainer>
  );
};

export default TimeSeriesChart;